import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from './config/api';

const ModalFichas = ({ isOpen, onClose, onEditarFicha, onNovaFicha, userLogado }) => {
  const [fichas, setFichas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('todos'); // todos, ativas, preenchidas, fechadas
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [dropdownAberto, setDropdownAberto] = useState(null);
  const itemsPorPagina = 10;

  useEffect(() => {
    if (isOpen) {
      carregarFichas();
    }
  }, [isOpen, busca, carregarFichas]);

  const carregarFichas = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      
      // Se houver busca, usar limite maior para paginação
      // Caso contrário, deixar o backend decidir (30 fichas mais recentes)
      if (busca.trim()) {
        params.search = busca;
        params.limit = 50;
      }
      
      const response = await axios.get(`${API_CONFIG.API_URL}/fichas`, {
        headers: {
          'x-auth-token': token
        },
        params
      });

      if (response.data.success && Array.isArray(response.data.data)) {
        setFichas(response.data.data);
      } else {
        setFichas([]);
      }
    } catch (error) {
      setFichas([]);
    } finally {
      setLoading(false);
    }
  }, [busca]);

  const fichasFiltradas = fichas.filter(ficha => {
    // Filtro de status
    if (filtro === 'ativas' && ficha.status === 'Fechada') return false;
    if (filtro === 'preenchidas' && ficha.status !== '' && ficha.status !== 'Preenchida') return false;
    if (filtro === 'fechadas' && ficha.status !== 'Fechada') return false;

    // Filtro de busca
    if (busca.trim()) {
      const termo = busca.toLowerCase();
      return (
        ficha.fichaId.toLowerCase().includes(termo) ||
        (ficha.cliente?.nome || '').toLowerCase().includes(termo) ||
        (ficha.cliente?.cpf || '').includes(termo)
      );
    }

    return true;
  });

  const totalPaginas = Math.ceil(fichasFiltradas.length / itemsPorPagina);
  const fichasPaginadas = fichasFiltradas.slice(
    (pagina - 1) * itemsPorPagina,
    pagina * itemsPorPagina
  );

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Aprovada':
      case 'Com possibilidade':
        return '#27ae60';
      case 'Recusada':
      case 'Fechada':
        return '#e74c3c';
      case 'Enviada para análise':
        return '#3498db';
      default:
        return '#95a5a6';
    }
  };

  // Capitalizar primeiro nome de cada palavra
  const capitalizeName = (name) => {
    if (!name) return '-';
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };


  if (!isOpen) return null;

  return (
    <div className="modal-overlay-preencher">
      <div className="modal-fichas">
        <button className="modal-close-btn" onClick={onClose} title="Fechar">×</button>

        <div className="modal-preencher-header">
          <h2>Gerenciar Fichas</h2>
        </div>

        <div className="modal-fichas-toolbar">
          <div className="toolbar-left">
            <input
              type="text"
              placeholder="Buscar por ID, cliente ou CPF..."
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setPagina(1);
              }}
              className="busca-input"
            />
          </div>

          <div className="toolbar-right">
            <select
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value);
                setPagina(1);
              }}
              className="filtro-select"
            >
              <option value="todos">Todos os Status</option>
              <option value="ativas">Ativas</option>
              <option value="preenchidas">Preenchidas</option>
              <option value="fechadas">Fechadas</option>
            </select>

            <button className="btn-nova-ficha" onClick={onNovaFicha}>
              + Nova Ficha
            </button>
          </div>
        </div>

        <div className="modal-fichas-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Carregando fichas...</p>
            </div>
          ) : fichasPaginadas.length === 0 ? (
            <div className="empty-state">
              <p>
                {fichas.length === 0
                  ? 'Nenhuma ficha criada ainda'
                  : 'Nenhuma ficha encontrada com os filtros aplicados'}
              </p>
              {fichas.length === 0 && (
                <button className="btn-nova-ficha-empty" onClick={onNovaFicha}>
                  Criar Primeira Ficha
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Tabela Desktop */}
              <table className="fichas-table fichas-table-desktop">
                <thead>
                  <tr>
                    <th>ID Ficha</th>
                    <th>Cliente</th>
                    <th>CPF</th>
                    <th>Data Criação</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {fichasPaginadas.map((ficha) => (
                    <tr key={ficha._id} className="ficha-row">
                      <td className="id-ficha">{ficha.fichaId}</td>
                      <td className="cliente-nome">{capitalizeName(ficha.cliente?.nome)}</td>
                      <td className="cpf">{ficha.cliente?.cpf || '-'}</td>
                      <td className="data">{formatarData(ficha.dataCriacao)}</td>
                      <td className="status">
                        <span
                          className="status-badge"
                          style={{ borderColor: getStatusColor(ficha.status) }}
                        >
                          {ficha.status}
                        </span>
                      </td>
                      <td className="acoes">
                        <button
                          className="btn-action btn-editar"
                          onClick={() => {
                            onEditarFicha(ficha);
                            onClose();
                          }}
                          title="Editar ficha"
                        >
                          ✏️ Editar
                        </button>
                        <button
                          className="btn-action btn-simulacao"
                          title="Ver simulações"
                        >
                          📊 Sim.
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Cards Mobile */}
              <div className="fichas-cards-mobile">
                {fichasPaginadas.map((ficha) => (
                  <div key={ficha._id} className="ficha-card-mobile">
                    <div className="ficha-card-header">
                      <span className="ficha-label">Cliente:</span>
                      <span className="ficha-cliente">{capitalizeName(ficha.cliente?.nome)}</span>
                    </div>
                    
                    <div className="ficha-card-body">
                      <div className="ficha-status-row">
                        <span className="ficha-label">Status:</span>
                        <span
                          className="status-badge"
                          style={{ borderColor: getStatusColor(ficha.status) }}
                        >
                          {ficha.status}
                        </span>
                      </div>
                    </div>

                    <div className="ficha-card-footer">
                      <button
                        className="btn-action btn-editar"
                        onClick={() => {
                          onEditarFicha(ficha);
                          onClose();
                        }}
                        title="Editar ficha"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        className="btn-action btn-simulacao"
                        title="Ver simulações"
                      >
                        📊 Sim.
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {totalPaginas > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setPagina(Math.max(1, pagina - 1))}
                    disabled={pagina === 1}
                    className="btn-pag"
                  >
                    Anterior
                  </button>

                  <div className="pagina-info">
                    Página {pagina} de {totalPaginas}
                    <span className="total-items">
                      ({fichasFiltradas.length} ficha
                      {fichasFiltradas.length !== 1 ? 's' : ''})
                    </span>
                  </div>

                  <button
                    onClick={() => setPagina(Math.min(totalPaginas, pagina + 1))}
                    disabled={pagina === totalPaginas}
                    className="btn-pag"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalFichas;
